import { type MouseEvent, memo } from "react"
import type { DynamicSitePackage } from "../../../../types"
import { Check, Close } from "@mui/icons-material"
import { Box, Stack, Typography, Button } from "@mui/material"
import { twMerge } from "tailwind-merge"
import { dateTimeToApiString } from "../../../../utils/dateTimeUtils"
import type { Step2Props } from "../types"
import { useUpdateWithoutRerender } from "../../../../api/activationObject/hooks"

interface ServicePackageItem {
    item: DynamicSitePackage
    handleButtonClick: () => void
}

type ServicePackageProps = ServicePackageItem & Pick<Step2Props, "activationObject" | "form" | "conditions" | "options" | "keyModalActions">

export const ServicePackage = memo((props: ServicePackageProps) => {
    const { item, activationObject, keyModalActions, form, handleButtonClick } = props
    const { setValue, watch } = form.form

    const handoverDate = watch("handoverDate")
    const backHandoverDate = watch("backHandoverDate")
    const handoverHour = watch("handoverHour")
    const backHandoverHour = watch("backHandoverHour")

    const updateMutation = useUpdateWithoutRerender()

    return (
        <Box
            sx={{
                border: "1px solid",
                boxShadow:
                    "5px 7px 19px 0px rgba(0, 0, 0, 0.03), 20px 27px 34px 0px rgba(0, 0, 0, 0.03), 45px 62px 46px 0px rgba(0, 0, 0, 0.02), 80px 110px 54px 0px rgba(0, 0, 0, 0.01), 126px 172px 60px 0px rgba(0, 0, 0, 0.00);",
            }}
            className={twMerge("border-pantone-yellow rounded-md py-[1.31rem] px-[0.687rem] w-72", !item.isFavorite && "border-none")}
        >
            <Stack justifyContent="center" className="px-[1.625rem]">
                <Typography className={twMerge("font-semibold text-sm text-pantone-yellow uppercase pb-[1.31rem]", !item.isFavorite && "opacity-0")}>
                    doporučujeme
                </Typography>
                <Typography className="text-[1.75rem] leading-[1.25rem] pb-[0.875rem] text-pantone-gray-2">{item.title}</Typography>
                <Typography className="text-xs font-thin min-h-[2rem] mb-[0.875rem]">{item.subTitle}</Typography>
                <Stack direction="row" justifyContent="center" alignItems="center" className="gap-[0.25rem] pb-[0.875rem]">
                    <Typography className="text-[2.625rem] text-pantone-black-100 font-semibold">{item.price}</Typography>
                    <Stack alignItems="flex-start" className="text-[0.875rem]">
                        <Typography className="font-semibold text-inherit text-pantone-black-100 leading-none">{item.unitPrice}</Typography>
                        <Typography className="font-light text-inherit text-pantone-black-100 leading-none">/měsíc</Typography>
                    </Stack>
                </Stack>
            </Stack>
            <Stack className="gap-2 h-[8.94rem] mb-[1.31rem]">
                {item.texts?.map((text, index) => (
                    <Stack key={index} justifyContent="flex-start" alignItems="flex-start" direction="row" className="gap-[0.25rem]">
                        {text.isCheck ? <Check className="text-green-700" fontSize="small" /> : <Close className="text-red-500" fontSize="small" />}
                        <Typography className={twMerge("text-xs font-light text-left", text.isBold && "font-bold")}>{text.title || ""}</Typography>
                    </Stack>
                ))}
            </Stack>
            <Button
                fullWidth
                variant={item.isFavorite ? "contained" : "outlined"}
                className={twMerge(
                    "normal-case shadow-none py-[0.63rem] text-xl leading-none",
                    !item.isFavorite && "border-pantone-gray-2 text-pantone-gray-2"
                )}
                type="button"
                onClick={(e: MouseEvent) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleButtonClick()
                    if (activationObject) {
                        const { actualWizardStep, previousWizardStep, ...rest } = activationObject
                        updateMutation.mutate({
                            ...rest,
                            destroyKey: false,
                            selectService: item.typePackage,
                        })
                    }
                    setValue("selectService", item.typePackage)
                    item.packageSelectionAction === "TakeKey"
                        ? keyModalActions.handleTakeKeyModalSwitch()
                        : item.packageSelectionAction === "BackKey" && keyModalActions.handleBackKeyModalSwitch()

                    if (activationObject && item.packageSelectionAction === "Invoicing") {
                        const { actualWizardStep, previousWizardStep, ...rest } = activationObject

                        const handoverKeysDateTime = handoverDate
                        const backHandoverKeysDateTime = backHandoverDate

                        if (handoverKeysDateTime) {
                            handoverKeysDateTime.setHours(handoverHour || 0)
                            handoverKeysDateTime.setMinutes(0)
                        }

                        if (backHandoverKeysDateTime) {
                            backHandoverKeysDateTime.setHours(backHandoverHour || 0)
                            backHandoverKeysDateTime.setMinutes(0)
                        }
                        updateMutation.mutate({
                            ...rest,
                            selectService: item.typePackage,
                            destroyKey: false,
                            handoverKeysDateTime: handoverKeysDateTime ? dateTimeToApiString(handoverKeysDateTime) : null,
                            backHandoverKeysDateTime: backHandoverKeysDateTime ? dateTimeToApiString(backHandoverKeysDateTime) : null,
                        })
                        keyModalActions.handleInvoiceRedirect()
                    }
                }}
            >
                vybrat službu
            </Button>
        </Box>
    )
})
